import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { NAV } from "@/config/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

export default function Sidebar({ collapsed }) {
  const location = useLocation();
  const { user, can, canViewPage, dwr } = useAuth();

  const visible = (item) => {
    if (item.superAdminOnly) return user?.role === "SuperAdmin";
    // Review screens: only for people who supervise someone, or management.
    if (item.dwrReview) return canViewPage("dwr") && !!(dwr?.is_supervisor || dwr?.is_management);
    if (item.module) return can(item.module, "view");
    if (item.page) return canViewPage(item.page);
    return true; // Dashboard and any item with no permission key
  };
  const filterVisible = (items) => items.filter(visible);
  const visibleNav = filterVisible(NAV)
    .map((n) => n.children ? { ...n, children: filterVisible(n.children) } : n)
    .filter((n) => !n.children || n.children.length > 0);

  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    visibleNav.forEach((n) => {
      if (n.children) {
        const active = n.children.some((c) => location.pathname.startsWith(c.path));
        if (active) initial[n.label] = true;
      }
    });
    return initial;
  });

  const toggle = (label) => setOpenGroups((s) => ({ ...s, [label]: !s[label] }));

  return (
    <aside
      data-testid="app-sidebar"
      className={cn(
        "h-screen sticky top-0 glass-strong border-r border-border transition-all duration-300 flex flex-col",
        collapsed ? "w-20" : "w-72"
      )}
    >
      {/* Brand */}
      <div className="px-5 py-5 border-b border-border flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl overflow-hidden shadow-lg shadow-primary/20 shrink-0">
          <img src="/img/karibunet-logo.jpg" alt="KaribuNet" className="w-full h-full object-cover" />
        </div>
        {!collapsed && (
          <div className="flex flex-col leading-tight">
            <span className="font-display text-base font-semibold tracking-tight">KARIS</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">KaribuNet Limited</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {visibleNav.map((item) => {
          const Icon = item.icon;
          if (item.children) {
            const isOpen = openGroups[item.label];
            const anyActive = item.children.some((c) => location.pathname.startsWith(c.path));
            return (
              <div key={item.label}>
                <button
                  data-testid={`sidebar-group-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => toggle(item.label)}
                  className={cn(
                    "w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors",
                    anyActive && "text-foreground"
                  )}
                >
                  <span className="flex items-center gap-3">
                    <Icon className={cn("w-4 h-4", anyActive && "text-primary")} />
                    {!collapsed && <span>{item.label}</span>}
                  </span>
                  {!collapsed && (isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
                </button>
                {!collapsed && isOpen && (
                  <div className="ml-2 mt-1 space-y-0.5 border-l border-border pl-2">
                    {item.children.map((child) => {
                      const CIcon = child.icon;
                      const active = location.pathname.startsWith(child.path);
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          data-testid={`sidebar-link-${child.path.replace(/\//g, "-")}`}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                            active
                              ? "sidebar-active text-foreground font-medium"
                              : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
                          )}
                        >
                          <CIcon className={cn("w-3.5 h-3.5", active && "text-primary")} />
                          <span>{child.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          const active = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`sidebar-link-${item.path.replace(/\//g, "-")}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
                active
                  ? "sidebar-active font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
              )}
            >
              <Icon className={cn("w-4 h-4", active && "text-primary")} />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-border text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {!collapsed ? "© 2026 KaribuNet" : "KN"}
      </div>
    </aside>
  );
}
